import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cn } from '@eigtech/ui/lib/utils'

/**
 * Flex container that wraps children onto multiple lines with consistent gap. Renders as a `div`
 * with `flex flex-wrap gap-2`. Use with `WrapItem` for aligned items. Has `data-slot="wrap"`.
 *
 * @example
 * ```tsx
 * <Wrap>
 *   <WrapItem>One</WrapItem>
 *   <WrapItem>Two</WrapItem>
 * </Wrap>
 * ```
 */
export function Wrap({ render, className, ...props }: useRender.ComponentProps<'div'>) {
  return useRender({
    defaultTagName: 'div',
    render,
    props: mergeProps<'div'>({ className: cn('flex flex-wrap gap-2', className) }, props),
    state: {
      slot: 'wrap',
    },
  })
}

/**
 * Single item in a `Wrap` layout. Renders as a `div` with `flex items-start`. Has `data-slot="wrap-item"`.
 */
export function WrapItem({ render, className, ...props }: useRender.ComponentProps<'div'>) {
  return useRender({
    defaultTagName: 'div',
    render,
    props: mergeProps<'div'>({ className: cn('flex items-start', className) }, props),
    state: {
      slot: 'wrap-item',
    },
  })
}
